<script setup>
  import {computed, inject, onMounted, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentPrivacyGuard from "@components/Alerts/PrivacyGuard.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentTab from "@components/Tab/Tab.vue";
  import ComponentTabs from "@components/Tab/Tabs.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {useForm, usePage} from "@inertiajs/vue3";
  import {bloodpressureKey, pulseKey} from "@pages/Records/Components/Sections/LaborBar/enums.js";
  import {MedButton, MedCheckbox, MedFormField, MedLabel, MedLink} from "@pharma4u/medicheck-components";
  import {usePrivacyGuard} from "@utils/Helpers/PrivacyGuard.js";

  defineOptions({name: "ComponentRecordShareDialog"});

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: [Object, null],
      default: null,
    },
    laborvalues: {
      type: [Object, null],
      default: null,
    },
    sharedata: {
      type: [Object, null],
      default: null,
    },
  });

  const pageProperties = usePage().props;
  const {validatePrivacy} = usePrivacyGuard();

  const privacy = inject("$privacy");

  const refDialog = ref(null);
  const refShareLinkUrl = ref(null);

  const currentPatient = ref(props.patient);
  const currentRecord = ref(props.record);
  const currentSharedata = ref(props.sharedata);
  const currentLaborvalues = ref(props.laborvalues);
  const isLoading = ref(false);
  const shareLinkUrl = ref(null);
  const copyingShareLinkUrl = ref(false);

  const activeTab = ref("direct");

  // checklist for expertservice
  const hasPulseAndBloodpressure = ref(false);
  const hasMainMedicalComplaints = ref(false);
  const hasKnownIndications = ref(false);
  const hasPrioritisedResult = ref(false);

  // error dialog
  const refServerErrorDialog = ref(null);
  const serverErrorTitle = ref("");
  const serverErrorContent = ref("");

  const tabTitles = {
    direct: "Direktfreigabe",
    link: "Linkfreigabe",
    expert: "Ärzteservice",
  };

  let form = useForm({
    email: "",
    notify_recipient: true,
    info: null,
    aliasname: "",
    meta: {
      contact_name:
        currentRecord.value?.username ??
        ((pageProperties?.client?.first_name ?? " ") + " " + (pageProperties?.client?.last_name ?? " ")).trim() ??
        null,
      contact_company: pageProperties?.client?.company ?? null,
      contact_email: pageProperties?.client?.email ?? null,
      contact_telephone: pageProperties?.client?.telephone ?? null,
      contact_information: null,
    },
  });

  onMounted(() => {
    privacy.decryptPatient(props.patient).then((decryptedPatient) => {
      currentPatient.value = decryptedPatient;
    });
  });

  watch(
    () => props.sharedata,
    (newSharedata) => {
      currentSharedata.value = newSharedata;
    },
  );

  const dialogTitle = computed(() => {
    switch (activeTab.value) {
      case "direct": {
        if (form.wasSuccessful) {
          return "Variante wurde geteilt";
        } else if (form.errors.length > 0) {
          return "Variante konnte nicht geteilt werden";
        }
        return "Mit wem möchten Sie diese Variante teilen?";
      }
      case "link": {
        if (form.wasSuccessful) {
          return "Seminar- / Linkfreigabe wurde erstellt";
        } else if (form.errors.length > 0) {
          return "Seminar - / Linkfreigabe konnte nicht erstellt werden";
        }
        return "Seminar- / Linkfreigabe";
      }
      case "expert": {
        if (form.wasSuccessful) {
          return "Variante wurde mit unserem Ärzteservice geteilt";
        } else if (form.errors.length > 0) {
          return "Variante konnte nicht an unseren Ärzteservice weitergeleitet werden";
        }
        return "Fragen Sie unseren Ärzteservice";
      }
      // No default
    }

    return "";
  });

  const hasSignedGvv = computed(() => pageProperties.can.gvv);

  const expertServiceRecipientMail = computed(
    () => import.meta.env?.VITE_EXPERT_SERVICE_RECIPIENT_MAIL ?? "medicheck-expertenteam@pharma4u.de",
  );

  const alreadySharedWithExpertService = computed(() => {
    return currentSharedata.value?.shared_by_me_with_expert_service ?? false;
  });

  const privacyErrors = computed(() => {
    return {
      aliasname: validatePrivacy(form.aliasname, "Alias-Name"),
      info: validatePrivacy(form.info, "Text"),
      contact_information: validatePrivacy(form.meta.contact_information, "Kontaktinformationen"),
    };
  });

  const tabChanged = (newActiveTabTitle) => {
    form.clearErrors();
    form.wasSuccessful = false;

    hasPulseAndBloodpressure.value =
      (currentLaborvalues.value?.data ?? []).some(
        (laborvalue) => laborvalue.mc_laborvalue_key === bloodpressureKey && laborvalue.value !== null,
      ) &&
      (currentLaborvalues.value?.data ?? []).some(
        (laborvalue) => laborvalue.mc_laborvalue_key === pulseKey && laborvalue.value !== null,
      );
    hasKnownIndications.value = false;
    hasMainMedicalComplaints.value = false;
    hasPrioritisedResult.value = false;

    // normalize internal "activeTab" according to title string
    switch (newActiveTabTitle) {
      case tabTitles.direct: {
        activeTab.value = "direct";
        break;
      }
      case tabTitles.link: {
        activeTab.value = "link";
        break;
      }
      case tabTitles.expert: {
        activeTab.value = "expert";
        break;
      }
    }
  };

  function open(record, initialTab) {
    activeTab.value = Object.keys(tabTitles).includes(initialTab) ? initialTab : "direct";
    // set defaults
    form.email = "";
    form.notify_recipient = true;
    form.aliasname = props.patient?.aliasname ?? "";

    if (record !== undefined) {
      currentRecord.value = record;

      if (record.sharedata !== undefined) {
        currentSharedata.value = record.sharedata;
      }
    }

    privacy.decryptValue(currentRecord.value.info).then((plaintext) => {
      if (plaintext !== null) {
        form.info = plaintext;
      }
    });

    // set initial value if record contains bloodpressure and pulse
    hasPulseAndBloodpressure.value =
      (currentLaborvalues.value?.data ?? []).some((laborvalue) => laborvalue.mc_laborvalue_key === bloodpressureKey) &&
      (currentLaborvalues.value?.data ?? []).some((laborvalue) => laborvalue.mc_laborvalue_key === pulseKey);

    // set initial value if record contains username
    if (currentRecord.value?.username) {
      form.meta.contact_name = currentRecord.value?.username;
    }

    refDialog.value.open();
  }

  function close() {
    form.reset();
    form.clearErrors();
    form.wasSuccessful = false;
    refDialog.value.close();
  }

  function save() {
    isLoading.value = true;
    form.clearErrors();

    switch (activeTab.value) {
      case "direct": {
        // store shared record
        form
          .transform((data) => {
            delete data.meta;
            return data;
          })
          .post(
            route("sharedrecords.store", {
              patient: props.patient.id,
              record: currentRecord.value.id,
            }),
            {
              preserveScroll: true,
              preserveState: true,
              only: ["flash", "errors", "records", "sharedata"],
              onFinish: () => {
                isLoading.value = false;
              },
              onError: (error) => {
                if (error.exception) {
                  console.error(error.exception);
                  serverErrorTitle.value = "Fehler bei Direktfreigabe";
                  serverErrorContent.value =
                    "Medikationsanalyse konnte nicht geteilt werden. Bitte versuchen Sie es später erneut.";
                  refServerErrorDialog.value.open();
                } else {
                  form.setError(
                    "server_side",
                    "Bei der Verarbeitung ist leider ein Fehler aufgetreten. Bitte prüfen Sie die E-Mail-Adresse des Empfängers.",
                  );
                }
              },
            },
          );

        break;
      }
      case "link": {
        // store share link
        form
          .transform((data) => {
            delete data.email;
            delete data.notify_recipient;
            delete data.meta;
            return data;
          })
          .post(
            route("sharelinks.store", {
              patient: props.patient.id,
              record: currentRecord.value.id,
            }),
            {
              preserveScroll: true,
              preserveState: true,
              only: ["flash", "errors", "records", "sharedata"],
              onFinish: () => {
                isLoading.value = false;
              },
              onSuccess: (response) => {
                shareLinkUrl.value = response.props.flash.payload;
                form.wasSuccessful = true;
              },
              onError: (error) => {
                if (error.exception) {
                  console.error(error.exception);
                  serverErrorTitle.value = "Fehler bei Linkfreigabe";
                  serverErrorContent.value =
                    "Linkfreigabe konnte nicht erstellt werden. Bitte versuchen Sie es später erneut.";
                  refServerErrorDialog.value.open();
                } else {
                  form.setError(
                    "server_side",
                    "Leider ist ein Fehler aufgetreten. Bitte überprüfen Sie die markierten Eingabefelder.",
                  );
                }
              },
            },
          );

        break;
      }
      case "expert": {
        // store expert service
        form
          .transform((data) => ({
            ...data,

            type: "expert",
            email: expertServiceRecipientMail.value,
            notify_recipient: true,
            isExpert: true,
          }))
          .post(
            route("sharedrecords.store", {
              patient: props.patient.id,
              record: currentRecord.value.id,
            }),
            {
              preserveScroll: true,
              preserveState: true,
              only: ["flash", "errors", "records", "sharedata"],
              onFinish: () => {
                isLoading.value = false;
              },
              onError: (error) => {
                if (error.exception) {
                  console.error(error.exception);
                  serverErrorTitle.value = "Fehler bei Freigabe an Ärzteservice";
                  serverErrorContent.value =
                    "Medikationsanalyse konnte nicht geteilt werden. Bitte versuchen Sie es später erneut.";
                  refServerErrorDialog.value.open();
                } else {
                  form.setError(
                    "server_side",
                    "Leider ist ein Fehler aufgetreten. Bitte überprüfen Sie die markierten Eingabefelder.",
                  );
                }
              },
            },
          );

        break;
      }
      // No default
    }
  }

  function copyToClipboard() {
    copyingShareLinkUrl.value = true;

    setTimeout(() => {
      copyingShareLinkUrl.value = false;
    }, 1000);

    navigator.clipboard.writeText(refShareLinkUrl.value.value).catch((error) => {
      console.error("Error while copying to clipboard:", error);
    });
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    dialog-width="medium"
    :dialog-title="dialogTitle"
    :is-loading="isLoading"
    :test-id="getTestId('record-share')"
  >
    <template #content>
      <ComponentTabs :active-tab="tabTitles[activeTab]" @click="tabChanged">
        <ComponentTab :title="tabTitles.direct" :test-id="getTestId('record-share-dialog-direct')">
          <div v-if="form.wasSuccessful" class="mt-6 space-y-4">
            <p>
              Erfolgreich geteilt mit:
              <br />
              <strong>{{ form.email }}</strong>
            </p>

            <p v-if="form.notify_recipient">
              Der Empfänger wird per E-Mail über Ihre Freigabe benachrichtigt und kann die geteilte Variante dort über
              einen Link öffnen bzw. bearbeiten.
            </p>

            <p v-else>
              Der Empfänger wird
              <em>nicht</em>
              per E-Mail benachrichtigt.
            </p>
          </div>

          <div v-else class="mt-6 grid grid-cols-3 gap-4">
            <ComponentInput
              v-model="form.email"
              class="col-span-2 col-start-1"
              label="E-Mail-Adresse des pharma4u-Accounts"
              :validation="form.errors.email"
              :test-id="getTestId('record-share-dialog-direct-tab-email')"
            />

            <ComponentInput
              v-model="form.aliasname"
              class="col-span-2 col-start-1"
              label="Alias-Name / Kurznotiz (max. 30 Zeichen)"
              maxlength="30"
              :validation="form.errors.aliasname"
              :privacy-guard="privacyErrors.aliasname"
              :test-id="getTestId('record-share-dialog-direct-tab-alias-name')"
            >
              <template #privacy-guard>
                <ComponentPrivacyGuard type="strict" :validation="privacyErrors.aliasname" />
              </template>
            </ComponentInput>

            <div class="col-span-3">
              <ComponentTextarea
                v-model="form.info"
                label="Weiteres zum Patienten"
                :validation="form.errors.info"
                :privacy-guard="privacyErrors.info"
                :test-id="getTestId('record-share-dialog-direct-tab-info')"
              >
                <template #privacy-guard>
                  <ComponentPrivacyGuard type="strict" :validation="privacyErrors.info" />
                </template>
              </ComponentTextarea>
            </div>

            <MedFormField orientation="horizontal" class="col-span-3">
              <MedCheckbox
                v-model="form.notify_recipient"
                variant="primary"
                :data-test="getTestId('record-share-dialog-direct-tab-notify-recipient-checkbox')"
              />

              <MedLabel>Empfänger per E-Mail benachrichtigen</MedLabel>
            </MedFormField>

            <div v-if="form.errors.server_side" class="col-span-3 text-mcred">{{ form.errors.server_side }}</div>
          </div>
        </ComponentTab>

        <ComponentTab
          v-if="$page.props.can.allow_shared_document_forwarding"
          :title="tabTitles.link"
          :test-id="getTestId('record-share-dialog-link')"
        >
          <div v-if="form.wasSuccessful" class="mt-6 space-y-4">
            <p>
              Die Seminar- / Linkfreigabe wurde erfolgreich erstellt. Bitte kopieren Sie sich den untenstehenden Link
              und senden Sie ihn an Ihren Kollegen / Teilnehmern.
            </p>

            <div class="flex flex-wrap items-center justify-between border-b border-gray-300 pb-6">
              <div class="flex items-center">
                <strong v-text="shareLinkUrl" />
                <!-- TODO: verify whether this hidden textarea is still needed - writeText() only takes a string, so shareLinkUrl.value could be passed directly; while it stays it needs tabindex="-1", because aria-hidden on a focusable element is invalid -->
                <textarea
                  ref="refShareLinkUrl"
                  aria-hidden="true"
                  style="width: 0; height: 0; opacity: 0"
                  tabindex="-1"
                  v-text="shareLinkUrl"
                />
              </div>

              <div>
                <MedButton
                  :icon="copyingShareLinkUrl ? 'check' : 'content_copy'"
                  :variant="copyingShareLinkUrl ? 'secondary' : 'primary'"
                  :data-test="getTestId('record-share-dialog-link-tab-copy-clipboard-button')"
                  @click="copyToClipboard()"
                >
                  Link kopieren
                </MedButton>
              </div>
            </div>

            <p>
              Wenn der Empfänger diesen Link aufruft, wird diese Variante umgehend freigegeben, so als hätten Sie eine
              direkte Fallfreigabe erstellt.
            </p>
          </div>

          <div v-else class="mt-6 space-y-4">
            <p>
              Bei einer Seminar- / Linkfreigabe erhalten Sie einen Link, den Sie ihren Kollegen / Teilnehmern bspw. per
              E-Mail zukommen lassen. Mit dem Aufruf dieses Links erhält ein Berechtigter Zugriff auf diese Variante.
            </p>

            <div class="grid grid-cols-3 gap-4">
              <ComponentInput
                v-model="form.aliasname"
                class="col-span-2 col-start-1"
                label="Alias-Name / Kurznotiz (max. 30 Zeichen)"
                maxlength="30"
                :validation="form.errors.aliasname"
                :privacy-guard="privacyErrors.aliasname"
                :test-id="getTestId('record-share-dialog-link-tab-alias-name')"
              >
                <template #privacy-guard>
                  <ComponentPrivacyGuard type="strict" :validation="privacyErrors.aliasname" />
                </template>
              </ComponentInput>

              <div class="col-span-3">
                <ComponentTextarea
                  v-model="form.info"
                  label="Weiteres zum Patienten"
                  :validation="form.errors.info"
                  :privacy-guard="privacyErrors.info"
                  :test-id="getTestId('record-share-dialog-link-tab-info')"
                >
                  <template #privacy-guard>
                    <ComponentPrivacyGuard type="strict" :validation="privacyErrors.info" />
                  </template>
                </ComponentTextarea>
              </div>

              <div v-if="form.errors.server_side" class="col-span-3 text-mcred">{{ form.errors.server_side }}</div>
            </div>
          </div>
        </ComponentTab>

        <ComponentTab
          v-if="$page.props.can.allow_expert_service"
          :title="tabTitles.expert"
          :test-id="getTestId('record-share-dialog-expert-service')"
        >
          <div v-if="!hasSignedGvv" class="mt-6 space-y-4">
            Wir freuen uns, Ihnen den MediCheck Ärzteservice vorstellen zu dürfen. Mit diesem neuen Service erhalten Sie
            fundierte Antworten auf Ihre Fragen zu Medikationsanalysen direkt von erfahrenen Ärzten.

            <MedLink
              variant="primary"
              href="https://medicheck.pharma4u.de/url-redirect/expert-service-information"
              target="_blank"
              :data-test="getTestId('record-share-dialog-expert-service-tab-more-info-link')"
            >
              Mehr Informationen
            </MedLink>

            <ComponentAlert type="info">
              Bitte beachten Sie, dass für Nutzung des Ärzteservice ein Vertrag zur gemeinsamen Verantwortlichkeit mit
              uns unterzeichnet werden muss. Dieser Vertrag garantiert die Sicherheit Ihrer Patientendaten unter
              Einhaltung der Datenschutzbestimmungen und regelt die datenschutzrechtlichen Verantwortlichkeiten im
              Rahmen der Nutzung des MediCheck Ärzteservice.
              <br />
              <br />

              Bitte klicken Sie auf den folgenden Link, um den Vertrag einzusehen und digital zu unterzeichnen:

              <MedLink
                variant="primary"
                href="https://medicheck.pharma4u.de/url-redirect/gvv-contract"
                target="_blank"
                :data-test="getTestId('record-share-dialog-expert-service-tab-gvv-contract-link')"
              >
                zum Vertrag
              </MedLink>
            </ComponentAlert>
          </div>
          <div v-else-if="form.wasSuccessful || alreadySharedWithExpertService" class="mt-6 space-y-4">
            <p>Ihre Variante wurde erfolgreich mit unserem Ärzteservice geteilt.</p>
            <div>
              <h6 class="font-semibold">Wie geht es weiter?</h6>
              <p class="mt-2">
                Ihr Fall wird innerhalb von ca. drei Werktagen von einem Experten der pharma4u geprüft und an Sie
                zurückgesendet. Einzelne Kommentare können Sie bereits vorab im Fall einsehen. Bei Rückfragen werden wir
                mit Ihnen Kontakt aufnehmen.
              </p>
            </div>
            <div v-if="alreadySharedWithExpertService" class="mt-2">
              <h6 class="font-semibold">Möchten Sie den Fall erneut senden?</h6>
              <p class="mt-2">
                Wenn Sie den Fall z. B. aufgrund von Änderungen am Analyseergebnis erneut an unseren Ärzteservice senden
                möchten, müssen Sie zunächst den bereits geteilten Fall widerrufen, bevor Sie den Fall erneut teilen
                können.
              </p>
            </div>
          </div>

          <div v-else class="mt-6 space-y-4">
            <template
              v-if="
                !hasPulseAndBloodpressure || !hasMainMedicalComplaints || !hasKnownIndications || !hasPrioritisedResult
              "
            >
              <p>
                Holen Sie eine ärztliche Zweitmeinung ein. Unsere MediCheck Ärzte können Ihnen bei Ihren Patientenfällen
                helfen.
                <br />
                Mehr Infos zu unserem
                <MedLink
                  variant="primary"
                  href="https://www.pharma4u.de/apotheker/medicheck/aerzteservice/"
                  target="_blank"
                  :data-test="getTestId('record-share-dialog-expert-service-tab-info-link')"
                >
                  Ärzteservice
                </MedLink>
              </p>

              <h6 class="font-semibold">Sind alle notwendigen Angaben eingetragen?</h6>
              <div class="grid grid-cols-3 gap-4">
                <MedLink
                  variant="primary"
                  href="https://medicheck.pharma4u.de/url-redirect/aerzteservice-sop"
                  target="_blank"
                  :data-test="getTestId('record-share-dialog-expert-service-tab-manual-link')"
                >
                  Hier geht's zur Anleitung
                </MedLink>
                <MedFormField
                  required
                  orientation="horizontal"
                  :disabled="
                    hasPulseAndBloodpressure && hasMainMedicalComplaints && hasKnownIndications && hasPrioritisedResult
                  "
                  class="col-span-3"
                >
                  <MedCheckbox
                    v-model="hasPulseAndBloodpressure"
                    variant="primary"
                    :data-test="getTestId('record-share-dialog-expert-service-tab-puls-blood-pressure-checkbox')"
                  />

                  <MedLabel>Vitalwerte (Blutdruck/ Puls) und bekannte Laborwerte (aktuell)</MedLabel>
                </MedFormField>

                <MedFormField
                  required
                  orientation="horizontal"
                  :disabled="
                    hasPulseAndBloodpressure && hasMainMedicalComplaints && hasKnownIndications && hasPrioritisedResult
                  "
                  class="col-span-3"
                >
                  <MedCheckbox
                    v-model="hasMainMedicalComplaints"
                    variant="primary"
                    :data-test="getTestId('record-share-dialog-expert-service-tab-medical-complaints-checkbox')"
                  />

                  <MedLabel>Probleme/ Symptome (mind. 1)</MedLabel>
                </MedFormField>

                <MedFormField
                  required
                  orientation="horizontal"
                  :disabled="
                    hasPulseAndBloodpressure && hasMainMedicalComplaints && hasKnownIndications && hasPrioritisedResult
                  "
                  class="col-span-3"
                >
                  <MedCheckbox
                    v-model="hasKnownIndications"
                    variant="primary"
                    :data-test="getTestId('record-share-dialog-expert-service-tab-known-indications-checkbox')"
                  />

                  <MedLabel>bekannte Indikationen</MedLabel>
                </MedFormField>

                <MedFormField
                  required
                  orientation="horizontal"
                  :disabled="
                    hasPulseAndBloodpressure && hasMainMedicalComplaints && hasKnownIndications && hasPrioritisedResult
                  "
                  class="col-span-3"
                >
                  <MedCheckbox
                    v-model="hasPrioritisedResult"
                    variant="primary"
                    :data-test="getTestId('record-share-dialog-expert-service-tab-prioritised-result-checkbox')"
                  />

                  <MedLabel>
                    Ich habe das Ergebnis priorisiert (Treffer markiert u./o. die Zusammenfassung geschrieben).
                  </MedLabel>
                </MedFormField>
              </div>
            </template>

            <template v-else>
              <div class="pt-4">
                <h6 class="font-semibold">Weitere Informationen zum Patienten</h6>
                <div class="grid grid-cols-3 gap-4">
                  <ComponentInput
                    v-model="form.aliasname"
                    class="col-span-2 col-start-1"
                    label="Alias-Name / Kurznotiz*"
                    maxlength="30"
                    :validation="form.errors.aliasname"
                    :privacy-guard="privacyErrors.aliasname"
                    :test-id="getTestId('record-share-dialog-expert-service-tab-alias-name')"
                  >
                    <template #privacy-guard>
                      <ComponentPrivacyGuard type="strict" :validation="privacyErrors.aliasname" />
                    </template>
                  </ComponentInput>

                  <div class="col-span-3">
                    <ComponentTextarea
                      v-model="form.info"
                      label="Weiteres zum Patienten"
                      :validation="form.errors.info"
                      :privacy-guard="privacyErrors.info"
                      :test-id="getTestId('record-share-dialog-expert-service-tab-info')"
                    >
                      <template #privacy-guard>
                        <ComponentPrivacyGuard type="strict" :validation="privacyErrors.info" />
                      </template>
                    </ComponentTextarea>
                  </div>
                </div>

                <h6 class="mt-4 font-semibold">Kontaktdaten</h6>
                <div class="grid grid-cols-4 gap-4">
                  <ComponentInput
                    v-model="form.meta.contact_name"
                    class="col-span-2"
                    label="Name Bearbeiter*"
                    :validation="form.errors['meta.contact_name']"
                    :test-id="getTestId('record-share-dialog-expert-service-tab-meta-contact-name')"
                  />

                  <ComponentInput
                    v-model="form.meta.contact_company"
                    class="col-span-2"
                    label="Apothekenname*"
                    :validation="form.errors['meta.contact_company']"
                    :test-id="getTestId('record-share-dialog-expert-service-tab-meta-contact-company')"
                  />

                  <ComponentInput
                    v-model="form.meta.contact_email"
                    class="col-span-2"
                    label="E-Mail*"
                    :validation="form.errors['meta.contact_email']"
                    :test-id="getTestId('record-share-dialog-expert-service-tab-meta-contact-email')"
                  />

                  <ComponentInput
                    v-model="form.meta.contact_telephone"
                    class="col-span-2"
                    label="Telefon*"
                    :validation="form.errors['meta.contact_telephone']"
                    :test-id="getTestId('record-share-dialog-expert-service-tab-meta-contact-telephone')"
                  />

                  <ComponentTextarea
                    v-model="form.meta.contact_information"
                    class="col-span-4"
                    label="Weitere Informationen zur Erreichbarkeit bei Rückfragen"
                    :validation="form.errors['meta.contact_information']"
                    :privacy-guard="privacyErrors.contact_information ?? undefined"
                    :test-id="getTestId('record-share-dialog-expert-service-tab-meta-contact-information')"
                  />
                </div>
              </div>
            </template>

            <div class="text-right text-sm">* Pflichtangaben</div>

            <div v-if="form.errors.server_side" class="col-span-3 text-mcred">{{ form.errors.server_side }}</div>
          </div>
        </ComponentTab>
      </ComponentTabs>
      <ComponentDialog
        ref="refServerErrorDialog"
        dialog-type="information"
        :dialog-title="serverErrorTitle"
        :dialog-content="serverErrorContent"
      />
    </template>

    <template #actions>
      <template v-if="!form.wasSuccessful">
        <template v-if="activeTab === 'direct'">
          <MedButton
            variant="primary"
            :disabled="form.processing || privacyErrors.aliasname || privacyErrors.info"
            :data-test="getTestId('record-share-dialog-save-button')"
            @click="save()"
          >
            Freigeben
          </MedButton>
        </template>

        <template v-else-if="activeTab === 'link'">
          <MedButton
            variant="primary"
            :disabled="form.processing || privacyErrors.aliasname || privacyErrors.info"
            :data-test="getTestId('record-share-dialog-save-button')"
            @click="save()"
          >
            Link erstellen
          </MedButton>
        </template>

        <template v-else-if="activeTab === 'expert'">
          <MedButton
            variant="primary"
            :disabled="
              form.processing ||
              privacyErrors.aliasname ||
              privacyErrors.info ||
              !hasPulseAndBloodpressure ||
              !hasMainMedicalComplaints ||
              !hasKnownIndications ||
              !hasPrioritisedResult ||
              !hasSignedGvv
            "
            :data-test="getTestId('record-share-dialog-save-button')"
            @click="save()"
          >
            Freigeben
          </MedButton>
        </template>
      </template>

      <MedButton :data-test="getTestId('record-share-dialog-close-button')" @click="close()">
        <template v-if="form.wasSuccessful">Schließen</template>
        <template v-else>Abbrechen</template>
      </MedButton>
    </template>
  </ComponentDialog>
</template>
